import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Tree } from '../../types/tree';
import './FilterPanel.css';

interface FilterPanelProps {
  trees: Tree[];
  selectedSpecies: string[];
  selectedConservation: string[];
  selectedSizes: string[];
  onSpeciesChange: (species: string[]) => void;
  onConservationChange: (status: string[]) => void;
  onSizeChange: (sizes: string[]) => void;
  onReset: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  trees,
  selectedSpecies,
  selectedConservation,
  selectedSizes,
  onSpeciesChange,
  onConservationChange,
  onSizeChange,
  onReset,
}) => {
  // Mobile detection for collapsed default state
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 480 : false
  );
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [showSpeciesDropdown, setShowSpeciesDropdown] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      // Auto-collapse when switching to mobile view
      if (mobile && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded]);

  // Get unique species sorted alphabetically
  const allSpecies = React.useMemo(() => {
    const speciesSet = new Set(trees.map(t => t.Species).filter(Boolean));
    return Array.from(speciesSet).sort();
  }, [trees]);

  // Count trees by conservation status
  const conservationCounts = React.useMemo(() => {
    const counts = {
      native: 0,
      endemic: 0,
      threatened: 0,
      invasive: 0,
      'non-native': 0,
    };

    trees.forEach(tree => {
      // Check if threatened using IUCN codes: VU (Vulnerable), EN (Endangered), CR (Critically Endangered), NT (Near Threatened)
      const threatStatus = String(tree.Threat_status || '').trim().toUpperCase();
      const isThreatened = threatStatus === 'VU' || threatStatus === 'EN' ||
                          threatStatus === 'CR' || threatStatus === 'NT';

      if (isThreatened) {
        counts.threatened++;
      }

      if (Number(tree.Invasive) === 1) {
        counts.invasive++;
      } else if (Number(tree.Endemism) === 1) {
        counts.endemic++;
      } else if (Number(tree.Native) === 1) {
        counts.native++;
      } else {
        counts['non-native']++;
      }
    });

    return counts;
  }, [trees]);

  // Count trees by size
  const sizeCounts = React.useMemo(() => {
    const counts = {
      'medium': 0,
      'large': 0,
      'extra-large': 0,
    };

    trees.forEach(tree => {
      const gbh = Number(tree.GBH) || 0;
      if (gbh >= 30 && gbh < 100) counts.medium++;
      else if (gbh >= 100 && gbh < 200) counts.large++;
      else if (gbh >= 200) counts['extra-large']++;
    });

    return counts;
  }, [trees]);

  const handleSpeciesToggle = (species: string) => {
    if (selectedSpecies.includes(species)) {
      onSpeciesChange(selectedSpecies.filter(s => s !== species));
    } else {
      onSpeciesChange([...selectedSpecies, species]);
    }
  };

  const handleConservationToggle = (status: string) => {
    if (selectedConservation.includes(status)) {
      onConservationChange(selectedConservation.filter(s => s !== status));
    } else {
      onConservationChange([...selectedConservation, status]);
    }
  };

  const handleSizeToggle = (size: string) => {
    if (selectedSizes.includes(size)) {
      onSizeChange(selectedSizes.filter(s => s !== size));
    } else {
      onSizeChange([...selectedSizes, size]);
    }
  };

  const activeFilterCount =
    selectedSpecies.length +
    selectedConservation.length +
    selectedSizes.length;

  return (
    <div className={`filter-panel ${isExpanded ? 'filter-panel--expanded' : ''}`}>
      <div className="filter-panel__header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="filter-panel__header-content">
          <Filter size={20} />
          <h3 className="filter-panel__title">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="filter-panel__badge">{activeFilterCount}</span>
          )}
        </div>
        <button className="filter-panel__toggle">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {isExpanded && (
        <div className="filter-panel__content">
          {/* Conservation Status Filter */}
          <div className="filter-panel__section">
            <h4 className="filter-panel__section-title">Conservation Status</h4>
            <div className="filter-panel__checkboxes">
              {Object.entries(conservationCounts).map(([status, count]) => (
                <label key={status} className="filter-panel__checkbox">
                  <input
                    type="checkbox"
                    checked={selectedConservation.includes(status)}
                    onChange={() => handleConservationToggle(status)}
                  />
                  <span className="filter-panel__checkbox-label">
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                    <span className="filter-panel__checkbox-count">({count})</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Size Filter */}
          <div className="filter-panel__section">
            <h4 className="filter-panel__section-title">Tree Size (GBH)</h4>
            <div className="filter-panel__checkboxes">
              <label className="filter-panel__checkbox">
                <input
                  type="checkbox"
                  checked={selectedSizes.includes('medium')}
                  onChange={() => handleSizeToggle('medium')}
                />
                <span className="filter-panel__checkbox-label">
                  Medium (30-100cm)
                  <span className="filter-panel__checkbox-count">({sizeCounts.medium})</span>
                </span>
              </label>
              <label className="filter-panel__checkbox">
                <input
                  type="checkbox"
                  checked={selectedSizes.includes('large')}
                  onChange={() => handleSizeToggle('large')}
                />
                <span className="filter-panel__checkbox-label">
                  Large (100-200cm)
                  <span className="filter-panel__checkbox-count">({sizeCounts.large})</span>
                </span>
              </label>
              <label className="filter-panel__checkbox">
                <input
                  type="checkbox"
                  checked={selectedSizes.includes('extra-large')}
                  onChange={() => handleSizeToggle('extra-large')}
                />
                <span className="filter-panel__checkbox-label">
                  Extra Large (&gt;200cm)
                  <span className="filter-panel__checkbox-count">({sizeCounts['extra-large']})</span>
                </span>
              </label>
            </div>
          </div>

          {/* Species Filter */}
          <div className="filter-panel__section">
            <h4 className="filter-panel__section-title">
              Species
              {selectedSpecies.length > 0 && (
                <span className="filter-panel__selected-count">
                  {selectedSpecies.length} selected
                </span>
              )}
            </h4>
            <div className="filter-panel__species-dropdown">
              <button
                className="filter-panel__species-button"
                onClick={() => setShowSpeciesDropdown(!showSpeciesDropdown)}
              >
                {selectedSpecies.length === 0
                  ? 'Select species...'
                  : `${selectedSpecies.length} species selected`}
                <ChevronDown size={16} />
              </button>

              {showSpeciesDropdown && (
                <div className="filter-panel__species-list">
                  {allSpecies.map(species => (
                    <label key={species} className="filter-panel__species-item">
                      <input
                        type="checkbox"
                        checked={selectedSpecies.includes(species)}
                        onChange={() => handleSpeciesToggle(species)}
                      />
                      <span>{species}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {selectedSpecies.length > 0 && (
              <div className="filter-panel__selected-species">
                {selectedSpecies.map(species => (
                  <span key={species} className="filter-panel__species-tag">
                    {species}
                    <button
                      onClick={() => handleSpeciesToggle(species)}
                      className="filter-panel__species-tag-remove"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Reset Button */}
          {activeFilterCount > 0 && (
            <button className="filter-panel__reset" onClick={onReset}>
              <X size={16} />
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
